import React from 'react';
import { Box, Typography, Tooltip, Alert, Button, CircularProgress } from '@mui/material';
import { InfoOutlined, ChevronRight, ChevronLeft } from '@mui/icons-material';

const GeneralSettingsTab = ({ tabProps }) => {
  const {
    programShortName,
    programId,
    tooltipProps,
    infoIconStyle,
    conditionsDirty,
    conditionsError,
    setConditionsError,
    conditionsLoading,
    conditionsChecking,
    disabledConditions,
    enabledConditions,
    selectedDisabled,
    selectedEnabled,
    getConditionDisplayName,
    handleToggleDisabledSelection,
    handleToggleEnabledSelection,
    handleMoveToEnabled,
    handleMoveToDisabled,
    stickyActionBarSx,
    renderDisabledInfoIcon,
    handleCancelConditions,
    handleApplyConditions,
  } = tabProps;

  return (
  <Box className="psTabContent">
    <Typography variant="h6" component="h2" className="psTabTitle">General Program Settings</Typography>
    <Typography variant="body1" sx={{ textAlign: 'center' }}>
      Program ID: {programShortName || programId}
    </Typography>

    <Box sx={{ mt: 3, textAlign: 'left' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          NBS Conditions to Enable:
        </Typography>
        <Tooltip
          title="This setting will enable and disable specific conditions for your program throughout the ED3N web app. Disabled conditions will not be flagged in patients, included in reports, or have clinical data displayed for them."
          {...tooltipProps}
        >
          <span tabIndex={0} role="button" aria-label="Information about NBS condition settings">
            <InfoOutlined style={{ ...infoIconStyle, marginLeft: '6px' }} />
          </span>
        </Tooltip>
        {conditionsDirty && (
          <Typography variant="caption" sx={{ ml: 2, color: 'var(--accent-primary-color)', fontWeight: 500 }}>
            * Unsaved changes
          </Typography>
        )}
      </Box>

      {conditionsError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setConditionsError('')}>
          {conditionsError}
        </Alert>
      )}

      <Box className="psTransferListContainer">
        <Box className="psTransferListPanel">
          <Typography variant="subtitle2" component="p" className="psTransferListLabel">
            Disabled Conditions
          </Typography>
          <Box className="psTransferListBox" role="listbox" aria-label="Disabled conditions">
            {conditionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : disabledConditions.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', p: 1, fontStyle: 'italic' }}>
                No disabled conditions
              </Typography>
            ) : (
              disabledConditions.map(condition => (
                <Box
                  key={condition}
                  role="option"
                  aria-selected={selectedDisabled.includes(condition)}
                  tabIndex={0}
                  className={`psTransferListItem ${selectedDisabled.includes(condition) ? 'selected' : ''}`}
                  onClick={() => handleToggleDisabledSelection(condition)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggleDisabledSelection(condition); } }}
                >
                  {getConditionDisplayName(condition)}
                </Box>
              ))
            )}
          </Box>
        </Box>

        <Box className="psTransferListArrows">
          <Button
            variant="outlined"
            size="small"
            onClick={handleMoveToEnabled}
            disabled={selectedDisabled.length === 0 || conditionsLoading}
            aria-label="Move selected to enabled"
            sx={{ minWidth: 36, p: 0.5 }}
          >
            <ChevronRight />
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleMoveToDisabled}
            disabled={selectedEnabled.length === 0 || conditionsLoading}
            aria-label="Move selected to disabled"
            sx={{ minWidth: 36, p: 0.5 }}
          >
            <ChevronLeft />
          </Button>
        </Box>

        <Box className="psTransferListPanel">
          <Typography variant="subtitle2" component="p" className="psTransferListLabel">
            Enabled Conditions
          </Typography>
          <Box className="psTransferListBox" role="listbox" aria-label="Enabled conditions">
            {conditionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : enabledConditions.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', p: 1, fontStyle: 'italic' }}>
                No enabled conditions
              </Typography>
            ) : (
              enabledConditions.map(condition => (
                <Box
                  key={condition}
                  role="option"
                  aria-selected={selectedEnabled.includes(condition)}
                  tabIndex={0}
                  className={`psTransferListItem ${selectedEnabled.includes(condition) ? 'selected' : ''}`}
                  onClick={() => handleToggleEnabledSelection(condition)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggleEnabledSelection(condition); } }}
                >
                  {getConditionDisplayName(condition)}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ ...stickyActionBarSx, mt: 1.5 }}>
        {!conditionsDirty && renderDisabledInfoIcon('No changes to apply')}
        <Button
          variant="outlined"
          onClick={handleCancelConditions}
          disabled={!conditionsDirty || conditionsLoading || conditionsChecking}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <Button
          variant={!conditionsDirty || conditionsLoading || conditionsChecking ? 'outlined' : 'contained'}
          color="primary"
          onClick={handleApplyConditions}
          disabled={!conditionsDirty || conditionsLoading || conditionsChecking}
          startIcon={(conditionsLoading || conditionsChecking) ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {(conditionsLoading || conditionsChecking) ? 'Applying...' : 'Apply'}
        </Button>
      </Box>
    </Box>
  </Box>
  );
};

export default GeneralSettingsTab;
